const KEY_PREFIX = 'larimar.auth.transient.';
const RETURN_TO_KEY = 'larimar.auth.returnTo';
const ESTABLISH_ATTEMPT_KEY = `${KEY_PREFIX}establishAttempted`;

export class TransientState {
  private constructor() {}

  static create(): TransientState {
    return new TransientState();
  }

  private isSameOrigin(value: string): boolean {
    try {
      return new URL(value, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  saveReturnTo(candidate?: string): void {
    const destination =
      candidate && this.isSameOrigin(candidate) ? candidate : window.location.origin;
    sessionStorage.setItem(RETURN_TO_KEY, destination);
  }

  takeReturnTo(): string | undefined {
    const stored = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return stored ?? undefined;
  }

  clearReturnTo(): void {
    sessionStorage.removeItem(RETURN_TO_KEY);
  }

  establishAlreadyAttempted(): boolean {
    return sessionStorage.getItem(ESTABLISH_ATTEMPT_KEY) === 'true';
  }

  markEstablishAttempted(): void {
    sessionStorage.setItem(ESTABLISH_ATTEMPT_KEY, 'true');
  }

  clearEstablishAttempt(): void {
    sessionStorage.removeItem(ESTABLISH_ATTEMPT_KEY);
  }

  private routeKey(): string {
    return `${KEY_PREFIX}scroll:${window.location.pathname}${window.location.search}`;
  }

  private getScrollContainer(): Element | Window {
    return document.querySelector('[data-scroll-container]') ?? window;
  }

  private readScrollPosition(target: Element | Window): { x: number; y: number } {
    if (target instanceof Element) {
      return { x: target.scrollLeft, y: target.scrollTop };
    }
    return { x: window.scrollX || 0, y: window.scrollY || 0 };
  }

  private maxScrollY(target: Element | Window): number {
    if (target instanceof Element) {
      return target.scrollHeight - target.clientHeight;
    }
    return document.documentElement.scrollHeight - window.innerHeight;
  }

  saveScrollPosition(): void {
    sessionStorage.setItem(
      this.routeKey(),
      JSON.stringify(this.readScrollPosition(this.getScrollContainer()))
    );
  }

  restoreScrollPosition(maxWaitMillis = 2000): void {
    const stored = sessionStorage.getItem(this.routeKey());
    if (!stored) {
      return;
    }
    sessionStorage.removeItem(this.routeKey());

    let position: { x: number; y: number };
    try {
      position = JSON.parse(stored) as { x: number; y: number };
    } catch {
      return;
    }
    if (!position.x && !position.y) {
      return;
    }

    const deadline = Date.now() + maxWaitMillis;
    const attempt = () => {
      const target = this.getScrollContainer();
      const reachable = this.maxScrollY(target) >= position.y;
      if (reachable || Date.now() > deadline) {
        target.scrollTo(position.x, position.y);
        return;
      }
      requestAnimationFrame(attempt);
    };
    attempt();
  }
}

export const transientState = TransientState.create();
